// Variable names for input-collecting questions.
//
// The kernel refuses to load a survey where an input-collecting question has no
// variable name — PresentorLoader._normalizeQuestion throws VARIABLE_REQUIRED
// before the first question is spoken. A name can go missing two ways: a node
// created without one, or an author clearing the field by accident. Neither
// should be able to break a survey, so new nodes get a name and blank ones are
// filled in silently when the descriptor is built.
//
// Names are derived from the question's own id rather than randomly generated.
// A derived name is stable: saving the same survey twice produces the same
// variable, so responses stay comparable across versions. A random one would
// change on every save.

/**
 * Turn a question id into a usable variable name.
 * "Q1754321" → "q1754321"; "My Question!" → "my_question"; "" → "q_<random>".
 */
const variableNameFromId = (seed) => {
  const cleaned = String(seed || '')
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9_]+/g, '_')
    .replace(/^_+|_+$/g, '');

  // Nothing usable in the id (blank, or punctuation only) — fall back to a
  // random suffix so the survey still loads.
  if (!cleaned) return `q_${Math.random().toString(36).slice(2, 10)}`;

  // Variable names must start with a letter.
  return /^[a-z]/.test(cleaned) ? cleaned : `q_${cleaned}`;
};

/**
 * Return `base`, or base_2 / base_3 ... if that name is already spoken for.
 */
const uniqueVariableName = (base, taken) => {
  if (!taken.has(base)) return base;
  let n = 2;
  while (taken.has(`${base}_${n}`)) n += 1;
  return `${base}_${n}`;
};

/**
 * Fill in blank variable names on a built descriptor's questions, in place.
 *
 * Only questions that actually need one are touched: an external-event node
 * (file upload, media) treats its variable as an optional result slot, and a
 * presentation-only node has no variable at all. Silent by design — a missing
 * name is a slip to repair, not something to interrupt the author about.
 *
 * @returns {number} how many names were filled in
 */
const backfillVariableNames = (questions = []) => {
  const taken = new Set(
    questions
      .map(q => String(q.variable_name || '').trim())
      .filter(Boolean)
  );

  let filled = 0;
  questions.forEach(q => {
    if (q.collectsInput !== true) return;
    if (q.node_class === 'external_event') return;
    if (String(q.variable_name || '').trim()) return;

    const base = variableNameFromId(q.display_id || q.question_id);
    const name = uniqueVariableName(base, taken);
    q.variable_name = name;
    taken.add(name);
    filled += 1;
  });
  return filled;
};

module.exports = { variableNameFromId, uniqueVariableName, backfillVariableNames };
